import { useState, useEffect } from "react";
import { Link } from "@tanstack/react-router";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { Button, cn } from "@provenleap/ui";
import { List, X, ArrowRight, Cube, PlugsConnected, PiggyBank, Question } from "@phosphor-icons/react";
import { Logo } from "../icons/Logo";
import type { Icon } from "@phosphor-icons/react";
export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const navLinks: { label: string; href: string; icon: Icon }[] = [
    { label: "Features", href: "#features", icon: Cube },
    { label: "Integrations", href: "#integrations", icon: PlugsConnected },
    { label: "Pricing", href: "#pricing", icon: PiggyBank },
    { label: "FAQ", href: "#faq", icon: Question },
  ];

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 backdrop-blur-md transition-[background-color,box-shadow]",
        // Translucent bar so the nav/logo read against white sections; lighter when
        // unscrolled so the hero still feels open, more solid once scrolled.
        scrolled ? "bg-white/80 shadow-md" : "bg-white/40 shadow-none",
      )}
    >
      <div className="max-w-[1200px] mx-auto px-3 sm:px-8 flex items-center justify-between h-16">
        {/* Logo */}
        <a href="#top" className="flex items-center">
          <Logo height={60} />
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-6" aria-label="Main navigation">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="flex items-center gap-2 text-[15px] font-medium text-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              <link.icon size={18} className="text-foreground" />
              {link.label}
            </a>
          ))}
        </nav>

        {/* Desktop actions */}
        <div className="hidden md:flex items-center gap-3">
          <Button asChild variant="brand">
            <Link to="/waitlist">
              Join Waitlist
              <ArrowRight size={16} className="ml-2" />
            </Link>
          </Button>
        </div>

        {/* Mobile menu button */}
        <button
          type="button"
          className="md:hidden inline-flex items-center justify-center min-h-11 min-w-11 text-muted-foreground hover:text-foreground"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? <X size={24} /> : <List size={24} />}
        </button>
      </div>

      {/* Mobile nav */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            className="md:hidden border-t border-[hsl(213_27%_86%)] bg-white px-4 py-4 space-y-3"
            aria-label="Mobile navigation"
            initial={{ opacity: 0, y: reduceMotion ? 0 : -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: reduceMotion ? 0 : -8 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-foreground py-2"
                onClick={() => setMobileOpen(false)}
              >
                <link.icon size={18} className="text-foreground" />
                {link.label}
              </a>
            ))}
            <div className="pt-3 border-t border-[hsl(213_27%_86%)]">
              <Button asChild variant="brand" className="w-full">
                <Link to="/waitlist" onClick={() => setMobileOpen(false)}>
                  Join Waitlist
                </Link>
              </Button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
